import { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetDashboardQuery } from "../../api/endpoints/dashboard";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { classifyLeagueView } from "./dashboardViewModel";
import { DashboardBucketList } from "./DashboardBucketList";
import { DeleteDraftDialog } from "../common/DeleteDraftDialog";
import { DASHBOARD_CARD_CLASS } from "./dashboardStyles";
import {
  DashboardViewSwitch,
  type SelectableDashboardView,
} from "./DashboardViewSwitch";
import { DashboardToolbar } from "./DashboardToolbar";
import {
  resolveEmptyState,
  LeagueDashboardEmptyState,
  LeagueDashboardLoadingSkeleton,
  LeagueDashboardErrorState,
} from "./LeagueDashboardEmptyStates";
import type { Clubhouse_LeagueOperatorDashboardLeagueDto } from "../../types/leagues";
import { LeaguesV2Events } from "../../analytics/leaguesV2Events";
import { useTrackLeaguesV2View } from "../../analytics/useTrackLeaguesV2View";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const SORT_LABEL: Record<SelectableDashboardView, string> = {
  IN_SEASON: "Leagues ending soonest appear first",
  UPCOMING: "Leagues with the earliest start dates appear first",
  FINISHED: "Leagues most recently completed appear first",
};

const VIEW_PARAM: Record<SelectableDashboardView, string> = {
  IN_SEASON: "in-season",
  UPCOMING: "upcoming",
  FINISHED: "finished",
};
const PARAM_TO_VIEW: Record<string, SelectableDashboardView> = {
  "in-season": "IN_SEASON",
  upcoming: "UPCOMING",
  finished: "FINISHED",
};

function DashboardHeader({ onNewLeague }: { onNewLeague: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <h1 className="poppins text-3xl font-extrabold tracking-[-0.02em] text-ink">
        Leagues
      </h1>
      <Button
        size="sm"
        className="gap-1.5 rounded-lg text-xs shadow-sm"
        onClick={onNewLeague}
      >
        <Plus className="size-4" /> New League
      </Button>
    </div>
  );
}

export function LeaguesDashboardPage() {
  const navigate = useNavigate();
  useTrackLeaguesV2View(LeaguesV2Events.VIEW_DASHBOARD);
  const goToNewLeague = () => navigate("/leagues/new");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const [facilityId, setFacilityId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  // The league whose delete confirmation is open. Kept apart from `open` so the
  // window keeps its text while it fades out after the row has gone.
  const [deleteTarget, setDeleteTarget] =
    useState<Clubhouse_LeagueOperatorDashboardLeagueDto | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Facility filtering is server-side: the dashboard API matches every
  // facility a league is offered at, not only its primary facilityId, so a
  // client-side `l.facilityId === facilityId` check would miss leagues.
  const { data, currentData, isLoading, isFetching, isError, refetch } =
    useGetDashboardQuery({ facilityId });
  // RTK Query keeps the previous facility's `data` while the new facility's
  // request is in flight; show the skeleton instead of stale rows/counts.
  const isSwitchingFacility = !!isFetching && currentData === undefined && data !== undefined;

  const now = useMemo(() => new Date(), []);
  const leagues = data?.leagues ?? [];

  const byView = useMemo(() => {
    const buckets: Record<SelectableDashboardView, typeof leagues> = {
      IN_SEASON: [],
      UPCOMING: [],
      FINISHED: [],
    };
    for (const league of leagues) {
      const view = classifyLeagueView(
        league.status,
        league.startDateUtc as unknown as string,
        league.statusSummary,
        now,
        league.endDateUtc as unknown as string,
        league.schedulingTimezone,
      );
      if (view === "EXCLUDED") continue;
      buckets[view].push(league);
    }
    return buckets;
  }, [leagues, now]);

  const rawCounts: Record<SelectableDashboardView, number> = {
    IN_SEASON: byView.IN_SEASON.length,
    UPCOMING: byView.UPCOMING.length,
    FINISHED: byView.FINISHED.length,
  };
  const totalLeaguesUnfiltered = leagues.length;

  const rawViewParam = searchParams.get("view");
  const requestedView =
    rawViewParam && Object.hasOwn(PARAM_TO_VIEW, rawViewParam)
      ? PARAM_TO_VIEW[rawViewParam]
      : undefined;

  // The default view is decided once, the first time dashboard data arrives,
  // and then locked in for the lifetime of the mount. A later background
  // refetch (several mutations elsewhere invalidate the "Dashboard" tag)
  // must not silently flip which view an operator is looking at just
  // because the IN_SEASON/UPCOMING split changed underneath them.
  const defaultViewRef = useRef<SelectableDashboardView | null>(null);
  if (defaultViewRef.current === null && data) {
    defaultViewRef.current =
      rawCounts.IN_SEASON === 0 && rawCounts.UPCOMING > 0 ? "UPCOMING" : "IN_SEASON";
  }
  const defaultView: SelectableDashboardView = defaultViewRef.current ?? "IN_SEASON";
  const view = requestedView ?? defaultView;

  function matchesFilters(l: Clubhouse_LeagueOperatorDashboardLeagueDto) {
    const term = search.trim().toLowerCase();
    return term === "" || (l.name ?? "").toLowerCase().includes(term);
  }

  // Counts shown in the view switch reflect the current search filter (and
  // the facility filter, already applied server-side) so all three stay in
  // sync with what searchFiltered shows for the active view.
  const filteredCounts: Record<SelectableDashboardView, number> = {
    IN_SEASON: byView.IN_SEASON.filter(matchesFilters).length,
    UPCOMING: byView.UPCOMING.filter(matchesFilters).length,
    FINISHED: byView.FINISHED.filter(matchesFilters).length,
  };

  const setView = (next: SelectableDashboardView) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("view", VIEW_PARAM[next]);
      return params;
    });
    setPage(1);
  };

  const searchFiltered = useMemo(
    () => byView[view].filter(matchesFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [byView, view, search],
  );

  function handleSearchChange(value: string) {
    setSearchInput(value);
    setPage(1);
  }
  function handleFacilityChange(value: string | undefined) {
    setFacilityId(value);
    setPage(1);
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 pb-4 md:px-6 md:pb-6">
        <DashboardHeader onNewLeague={goToNewLeague} />
        <div className="mt-4 flex items-center justify-between">
          <DashboardViewSwitch
            value="IN_SEASON"
            counts={undefined}
            onChange={() => { }}
          />
        </div>
        <div className={`mt-2 ${DASHBOARD_CARD_CLASS}`}>
          <LeagueDashboardLoadingSkeleton />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 pb-4 md:px-6 md:pb-6">
        <DashboardHeader onNewLeague={goToNewLeague} />
        <LeagueDashboardErrorState onRetry={refetch} />
      </div>
    );
  }

  const emptyState = resolveEmptyState({
    totalLeaguesUnfiltered,
    currentViewFilteredCount: searchFiltered.length,
    search,
    view,
    facilityFiltered: facilityId !== undefined,
  });

  if (!isSwitchingFacility && emptyState?.kind === "onboarding") {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 pb-4 md:px-6 md:pb-6">
        <h1 className="poppins text-3xl font-extrabold tracking-[-0.02em] text-ink">
          Leagues
        </h1>
        <p className="mulish mt-1 text-sm text-text-muted">
          Run leagues across your bays, with scoring, handicaps and standings done for
          you.
        </p>
        <div className={`mt-6 p-10 ${DASHBOARD_CARD_CLASS}`}>
          <LeagueDashboardEmptyState
            state={emptyState}
            onNewLeague={goToNewLeague}
            onClearSearch={() => setSearchInput("")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-4 md:px-6 md:pb-6">
      <DashboardHeader onNewLeague={goToNewLeague} />

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <DashboardViewSwitch
          value={view}
          counts={isSwitchingFacility ? undefined : filteredCounts}
          onChange={setView}
        />
        <DashboardToolbar
          search={searchInput}
          onSearchChange={handleSearchChange}
          facilityId={facilityId}
          onFacilityChange={handleFacilityChange}
        />
      </div>

      <p className="mt-5 text-xs text-text-faint">{SORT_LABEL[view]}</p>

      {isSwitchingFacility ? (
        <div className={`mt-2 ${DASHBOARD_CARD_CLASS}`}>
          <LeagueDashboardLoadingSkeleton />
        </div>
      ) : emptyState ? (
        <div className={`mt-2 ${DASHBOARD_CARD_CLASS}`}>
          <div className="p-10">
            <LeagueDashboardEmptyState
              state={emptyState}
              onNewLeague={goToNewLeague}
              onClearSearch={() => setSearchInput("")}
            />
          </div>
        </div>
      ) : (
        <DashboardBucketList
          view={view}
          leagues={searchFiltered}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          // Carry the bucket along so the league page's "Leagues" link can
          // return to the view the operator was actually reading. It cannot
          // get there from history: the round page pushes its way back to the
          // league, so the previous entry is often the round.
          onOpen={(leagueId) =>
            navigate(`/leagues/${leagueId}`, {
              state: { fromView: VIEW_PARAM[view] },
            })
          }
          onDelete={(league) => {
            setDeleteTarget(league);
            setIsDeleteOpen(true);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteDraftDialog
          open={isDeleteOpen}
          // No onDeleted: the row leaves through the dashboard refresh the
          // delete triggers, so there is nothing to do once it has gone.
          onOpenChange={setIsDeleteOpen}
          leagueId={deleteTarget.leagueId}
          facilityId={deleteTarget.facilityId ?? ""}
          name={deleteTarget.name ?? "this league"}
          leagueType={deleteTarget.type}
          roundCount={deleteTarget.statusSummary?.totalRounds ?? 0}
        />
      )}
    </div>
  );
}
